import { ChangeEvent, useEffect, useState } from 'react';

import { IconPlus } from '@iwoca/icons';
import {
  fetchGetDirectorSearch,
  GetDirectorSearchResponse,
} from '@iwoca/lapi-client/edge';
import { Button, Input, Label, Select } from '@iwoca/orion';
import { clsx } from 'clsx';
import { upperCase } from 'lodash-es';
import { useFormContext } from 'react-hook-form';

import styles from './DirectorSearch.module.css';
import { TPersonalDetailsDataUK } from '../PersonalDetailsUK';

type TProps = {
  companyName?: string;
  companyNumber?: string;
  includeMiddleName?: boolean;
};

export const DirectorSearch = ({
  companyName,
  companyNumber,
  includeMiddleName,
}: TProps) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [directorOptions, setDirectorOptions] = useState<
    GetDirectorSearchResponse['data']
  >([]);

  const {
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<TPersonalDetailsDataUK>();

  const [showDefaultName, setShowDefaultName] = useState(
    Boolean(getValues('firstName') && getValues('lastName')),
  );

  useEffect(() => {
    setSelectedOption('');
    const fetchDirectors = async () => {
      try {
        setIsLoading(true);
        const directors = await fetchGetDirectorSearch({
          companyNumber: companyNumber as string,
        });
        setDirectorOptions(directors.data);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        setDirectorOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (companyNumber) {
      fetchDirectors().catch((e) => {
        console.error(e, 'failed to fetch director info');
      });
    } else {
      setDirectorOptions([]);
    }
  }, [companyNumber]);

  const disabled = isLoading;

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(e.target.value);

    const director = directorOptions![parseInt(e.target.value, 10)];

    if (director.first_name) {
      const directorFirstAndMiddleNames = [
        director.first_name,
        director.middle_names,
      ]
        .filter(Boolean)
        .join(' ');

      const firstName = includeMiddleName
        ? directorFirstAndMiddleNames
        : director.first_name;

      setValue('firstName', firstName, {
        shouldValidate: true,
      });
    }

    if (director.last_name) {
      setValue('lastName', director.last_name, {
        shouldValidate: true,
      });
    }

    if (director.month_of_birth) {
      setValue('dobMonth', director.month_of_birth.toString(), {
        shouldValidate: true,
      });
    }

    if (director.year_of_birth) {
      setValue('dobYear', director.year_of_birth.toString(), {
        shouldValidate: true,
      });
    }
  };

  const buildNameValue = (
    firstName?: string,
    lastName?: string,
    isDirector?: boolean,
  ) => {
    return `${upperCase(lastName)}, ${firstName} ${
      isDirector ? '(Director)' : ''
    }`;
  };

  const renderOptions = () => {
    if (!directorOptions) {
      return <option disabled>No directors found</option>;
    } else
      return directorOptions?.map((person, index) => (
        <option key={index} value={index} className="fs-mask dd-privacy-mask">
          {buildNameValue(
            person.first_name,
            person.last_name,
            person.roles?.includes('director'),
          )}
        </option>
      ));
  };

  return (
    <>
      <Label htmlFor="director-search">Full name</Label>
      <div className={styles.directorSelect}>
        {selectedOption || showDefaultName ? (
          <>
            <Input
              className={clsx('fs-mask dd-privacy-mask', styles.fullNameInput)}
              disabled
              value={buildNameValue(
                getValues('firstName'),
                getValues('lastName'),
                getValues('isDirector'),
              )}
              description={`You will need to be a director of ${companyName}`}
            />
            <Button
              className={styles.clearButton}
              type="reset"
              variant="tertiary"
              size="small"
              data-testid="clear-button-director"
              onClick={() => {
                setSelectedOption('');
                setShowDefaultName(false);
              }}
            >
              <IconPlus
                className={styles.clearButtonIcon}
                height="24"
                width="24"
              />
            </Button>
          </>
        ) : (
          <Select
            defaultValue={selectedOption}
            onChange={handleChange}
            description={`You will need to be a director of ${companyName}`}
            className={clsx('fs-mask dd-privacy-mask', {
              [styles.errorContainer]:
                errors?.firstName?.message || errors?.lastName?.message,
            })}
            disabled={disabled}
            id="director-search"
            error={errors?.firstName?.message || errors?.lastName?.message}
          >
            <option value="" disabled>
              {disabled ? 'Loading directors...' : 'Select an option'}
            </option>
            {renderOptions()}
          </Select>
        )}
      </div>
    </>
  );
};
